<script setup>
import { defineProps, onMounted, ref, computed } from 'vue'
import axios from 'axios'
import { useUserStore } from '@/stores/user'
import VueJsonPretty from 'vue-json-pretty'
import 'vue-json-pretty/lib/styles.css'

const API_URL = process.env.VUE_APP_API_URL

const emit = defineEmits(['slideover-close'])

const userStore = useUserStore()

const is_running = ref(false)
const failed = ref(false)
const respone_obj = ref(null)

const props = defineProps({
  query: { type: String },
  method: { type: String },
  name: { type: String },
  body: { type: Object },
  download: { type: Boolean, default: false }
})

// Examples run as the logged-in viewer: the user store sets the global axios
// Authorization default at init, but we attach it explicitly here so the
// runner never silently issues an unauthenticated request.
const token = computed(() => userStore.token || localStorage.getItem('token') || '')
const isAuthed = computed(() => !!token.value)

const method = computed(() => (props.method || '').toUpperCase())

function authConfig() {
  return { headers: { Authorization: `Bearer: ${token.value}` } }
}

// For file downloads we can't render JSON, open a token-authenticated URL so
// the browser streams the attachment (mirrors src/api/Enrichments.js).
const downloadUrl = computed(() =>
  `${API_URL}${props.query}?token=${encodeURIComponent(token.value)}`
)

async function get() {
  try {
    let { data } = await axios.get(API_URL + props.query, authConfig())
    respone_obj.value = data
  } catch {
    failed.value = true
  }
}

async function post() {
  try {
    let { data } = await axios.post(API_URL + props.query, props.body, authConfig())
    respone_obj.value = data
  } catch {
    failed.value = true
  }
}

onMounted(async () => {
  // Download endpoints and unauthenticated viewers render a static panel
  // rather than firing a request.
  if (props.download || !isAuthed.value) {
    return
  }

  is_running.value = true

  if (method.value === 'GET') {
    await get()
  } else if (method.value === 'POST') {
    await post()
  } else {
    failed.value = true
  }

  is_running.value = false
})

const downloading = ref(false)

function filenameFromResponse(res) {
  const disposition = res.headers?.['content-disposition'] || ''
  const match = /filename="?([^"]+)"?/.exec(disposition)
  return match ? match[1] : 'export'
}

function triggerBlobDownload(res) {
  const url = window.URL.createObjectURL(new Blob([res.data]))
  const a = document.createElement('a')
  a.href = url
  a.download = filenameFromResponse(res)
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)
}

// GET downloads open a token-authenticated URL directly (mirrors
// src/api/Enrichments.js). POST downloads must send a body, so they stream the
// response as a blob and save it client-side.
async function runDownload() {
  failed.value = false
  if (method.value === 'POST') {
    downloading.value = true
    try {
      const res = await axios.post(API_URL + props.query, props.body, { ...authConfig(), responseType: 'blob' })
      triggerBlobDownload(res)
    } catch {
      failed.value = true
    }
    downloading.value = false
  } else {
    window.open(downloadUrl.value, '_blank')
  }
}
</script>

<template>
  <div class="api-slideover">
    <!-- Header -->
    <div class="box-header">
      <h3>
        <span v-if="method === 'GET'" class="method-badge get">GET</span>
        <span v-else-if="method === 'POST'" class="method-badge post">POST</span>
        <span v-else class="method-badge other">{{ props.method }}</span>
        {{ props.name }}
      </h3>
    </div>

    <!-- Content -->
    <div class="box-content">
      <!-- Not authenticated -->
      <div v-if="!isAuthed" class="state-container">
        <div class="state-icon">
          <font-awesome-icon style="font-size: 5rem;" :icon="['fas', 'lock']" />
        </div>
        <h3 class="state-title">Log in to run examples</h3>
        <p class="state-message">
          Live examples run as your account. Sign in and generate an API key from your
          account screen to try requests here.
        </p>
      </div>

      <!-- Download mode -->
      <div v-else-if="props.download" class="content-sections">
        <div class="menu-container mb-3">
          <h5 class="section-title">REQUEST</h5>
          <div class="box padded">
            <code class="request-url">{{ API_URL + props.query }}</code>
          </div>
        </div>

        <div v-if="props.body && method === 'POST'" class="menu-container mb-3">
          <h5 class="section-title">REQUEST BODY</h5>
          <div class="box padded">
            <vue-json-pretty :data="props.body" style="overflow: auto; max-height: 400px;"/>
          </div>
        </div>

        <div class="menu-container">
          <h5 class="section-title">RESPONSE</h5>
          <div class="box padded download-box">
            <p class="download-hint">
              This endpoint streams a file. Trigger the download to receive the
              attachment in your browser.
            </p>
            <p v-if="failed" class="download-error">
              Download failed. Ensure the request is valid and your account has
              access to this endpoint.
            </p>
            <button class="download-button" :disabled="downloading" @click="runDownload">
              <font-awesome-icon :icon="['fas', downloading ? 'gear' : 'download']" :spin="downloading" />
              {{ downloading ? 'Preparing...' : 'Download file' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-else-if="is_running" class="state-container">
        <div class="state-icon">
          <font-awesome-icon style="font-size: 5rem;" :icon="['fas', 'gear']" spin />
        </div>
        <h3 class="state-title">Sending...</h3>
        <p class="state-message">Your response will be ready in a second :)</p>
      </div>

      <!-- Error State -->
      <div v-else-if="failed" class="state-container">
        <div class="state-icon error">
          <font-awesome-icon style="font-size: 5rem; color: var(--color-error);" :icon="['fas', 'circle-exclamation']"/>
        </div>
        <h3 class="state-title">Failed</h3>
        <p class="state-message">Sorry! Something went wrong. Ensure that the request parameters are correct and that your account has access to this endpoint.</p>
      </div>

      <!-- Success State -->
      <div v-else class="content-sections">
        <!-- Request URL -->
        <div class="menu-container mb-3">
          <h5 class="section-title">REQUEST</h5>
          <div class="box padded">
            <code class="request-url">{{ API_URL + props.query }}</code>
          </div>
        </div>

        <!-- Request Body (POST only) -->
        <div v-if="props.body && method === 'POST'" class="menu-container mb-3">
          <h5 class="section-title">REQUEST BODY</h5>
          <div class="box padded">
            <vue-json-pretty :data="props.body" style="overflow: auto; max-height: 400px;"/>
          </div>
        </div>

        <!-- Response -->
        <div class="menu-container">
          <h5 class="section-title">RESPONSE</h5>
          <div class="box padded">
            <vue-json-pretty :data="respone_obj" style="overflow: auto; max-height: 600px;"/>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.api-slideover {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  color: var(--text-heading);
  font-family: var(--font-heading);
  margin: 0;
  padding: 0;
}

.box-header {
  background-color: var(--surface-overlay);
  padding: 1rem 2rem;
  border-bottom: 1px solid var(--border-subtle);
  flex-shrink: 0;
  width: 100%;
  margin: 0;
  border-radius: var(--radius-md) 0 0 0;
}

.box-header h3 {
  margin: 0;
  color: var(--text-primary);
  font-size: 1.1rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.method-badge {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  padding: 0.25rem 0.6rem;
  border-radius: var(--radius-pill);
}

.method-badge.get {
  color: var(--color-success-text);
  background-color: var(--color-success-soft);
}

.method-badge.post {
  color: var(--color-info-text);
  background-color: var(--color-info-soft);
}

.method-badge.other {
  color: var(--text-secondary);
  background-color: var(--surface-overlay-2);
}

.box-content {
  flex: 1;
  padding: 2rem;
  overflow-y: auto;
  min-height: 0;
  width: 100%;
  box-sizing: border-box;
}

/* State Containers (Loading/Error/Auth) */
.state-container {
  text-align: center;
  padding: 3rem 1rem;
}

.state-icon {
  margin-bottom: 1.5rem;
  color: var(--text-faint);
}

.state-icon.error {
  color: var(--color-error);
}

.state-title {
  color: var(--text-heading);
  margin-bottom: 0.75rem;
  font-size: 1.5rem;
}

.state-message {
  color: var(--text-muted);
  margin: 0;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
}

/* Content Sections */
.content-sections {
  width: 100%;
}

.menu-container {
  margin-bottom: 1.5rem;
}

.section-title {
  color: var(--text-primary);
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  letter-spacing: 0.05em;
}

.box {
  background-color: var(--surface-sunken);
  border-radius: var(--radius-lg);
}

.box.padded {
  padding: 1rem;
}

.request-url {
  color: var(--text-primary);
  word-break: break-all;
  font-family: var(--font-heading);
  font-size: 0.875rem;
}

.box :deep(.vjs-tree) {
  font-family: var(--font-heading);
}

.download-box {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.download-hint {
  color: var(--text-muted);
  font-size: 0.875rem;
  margin: 0;
}

.download-error {
  color: var(--color-error-text);
  font-size: 0.875rem;
  margin: 0;
}

.download-button:disabled {
  opacity: 0.6;
  cursor: default;
}

.download-button {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: var(--accent-soft-3);
  color: var(--accent-text);
  border: none;
  border-radius: var(--radius-md);
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
}

.download-button:hover {
  background-color: var(--accent-soft-4);
}

/* Custom scrollbar */
.box-content::-webkit-scrollbar {
  width: 6px;
}

.box-content::-webkit-scrollbar-track {
  background: var(--surface-raised);
}

.box-content::-webkit-scrollbar-thumb {
  background: var(--surface-control);
  border-radius: var(--radius-xs);
}
</style>
